import { AccessibilityType } from '@yoop/server-model/event-accessibility';
import { UiUserActivityTabType } from '@yoop/server-model/user-activity';
import { isNilOrEmpty, mapNonNil } from '@yoop/util';
import { apiClientStore } from '@yoop/whitelabel-api-client';
import type { MyYoopFilterOptions } from '@yoop/whitelabel-data';
import { CommsMessageActions, MyYoopTabType } from '@yoop/whitelabel-data';
import { appStore, communicationStore } from '@yoop/whitelabel-manager-store';
import isNil from 'ramda/src/isNil';
import {
  onAssignTokenToCurrentUser,
  onResendTokenInvite,
  onReturnToken,
  onRevokeToken,
} from './my-yoop-token-actions';
import {
  eventDetailDataFrom,
  myYoopEventFrom,
  myYoopRequestedEventFrom,
  publicEventDataFrom,
  upgradeOfferRequestFrom,
  resaleEventFrom,
} from '@yoop/whitelabel/data-mapper';

const tabTypeFrom = (type: UiUserActivityTabType): MyYoopTabType => {
  switch (type) {
    case UiUserActivityTabType.LIVE_ATTENDANCE_EVENTS:
      return MyYoopTabType.InPerson;
    case UiUserActivityTabType.ACTIVE_OFFER_EVENTS:
      return MyYoopTabType.Requested;
    case UiUserActivityTabType.STREAMING_EVENTS:
      return MyYoopTabType.Virtual;
    case UiUserActivityTabType.SEASON_MEMBERSHIPS:
      return MyYoopTabType.Membership;
    case UiUserActivityTabType.SELLING_EVENT:
      return MyYoopTabType.Selling;
    default:
      return undefined;
  }
};

const onGetInPersonEventList = async (requestKey: string, options?: MyYoopFilterOptions) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.InPersonEventsList,
    key: requestKey,
    getData: async () => {
      const { currentUser, clientId } = appStore.getState();
      const { apiClient } = apiClientStore.getState();
      const response = await apiClient.eventController.getUserEvents({
        clientId,
        type: AccessibilityType.LIVE_ATTENDANCE,
        dateFilterType: options?.dateFilterType as any, //types are the same, but duplicated due to nx buildable restriction
      });
      return response.userActivityList.map((event) => myYoopEventFrom(event, currentUser));
    },
  });
};

const onGetVirtualEventList = async (requestKey: string, options?: MyYoopFilterOptions) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.VirtualEventsList,
    key: requestKey,
    getData: async () => {
      const { currentUser, clientId } = appStore.getState();
      const { apiClient } = apiClientStore.getState();
      const response = await apiClient.eventController.getUserEvents({
        clientId,
        type: AccessibilityType.STREAMING,
        dateFilterType: options?.dateFilterType as any, //types are the same, but duplicated due to nx buildable restriction
      });
      return response.userActivityList.map((event) => myYoopEventFrom(event, currentUser));
    },
  });
};

const onGetRequestedEventList = async (requestKey: string, options?: MyYoopFilterOptions) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.RequestedEventsList,
    key: requestKey,
    getData: async () => {
      const { clientId } = appStore.getState();
      const { apiClient } = apiClientStore.getState();
      const response = await apiClient.eventController.getUserRequestedEvents({
        clientId,
        dateFilterType: options?.dateFilterType as any, //types are the same, but duplicated due to nx buildable restriction
      });
      return response.userActivityList.map((event) => myYoopRequestedEventFrom(event));
    },
  });
};

const onGetResaleEventList = async (requestKey: string, options?: MyYoopFilterOptions) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.ResaleEventsList,
    key: requestKey,
    getData: async () => {
      const { clientId } = appStore.getState();
      const { apiClient } = apiClientStore.getState();
      const response = await apiClient.eventController.getUserSellingEvents({
        clientId,
        dateFilterType: options?.dateFilterType as any,
      });
      return response.eventList.map((event) => resaleEventFrom(event));
    },
  });
};

const onGetUserTabs = async (requestKey: string) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.Tabs,
    key: requestKey,
    getData: async () => {
      const { clientId } = appStore.getState();
      const { apiClient } = apiClientStore.getState();
      const response = await apiClient.userController.getUserTabs(clientId);
      return mapNonNil(response.tabResponses, ({ tabType, showPastEvents, default: isDefault }) => {
        const type = tabTypeFrom(tabType);
        if (isNil(type)) {
          return undefined;
        }
        return {
          type,
          hasPastEvents: showPastEvents,
          isDefault,
        };
      });
    },
  });
};

const onGetEventDetail = async (
  eventId: string,
  marketplaceCode: string | undefined,
  requestKey: string,
) => {
  const { sendDataMessage } = communicationStore.getState();
  sendDataMessage({
    action: CommsMessageActions.EventDetail,
    key: requestKey,
    getData: async () => {
      const { currentUser } = appStore.getState();
      const { apiClient } = apiClientStore.getState();

      if (currentUser) {
        const response = await apiClient.eventController.getUserActivityDetails(
          eventId,
          marketplaceCode,
        );
        const upgradeRequest = upgradeOfferRequestFrom([
          ...(response.tokenInformation ?? []),
          ...(response.assigneeTokenInformation ?? []),
        ]);
        const upgradableOffers = new Set<number>();
        if (!isNilOrEmpty(upgradeRequest)) {
          try {
            const upgradeResult = await apiClient.eventController.getOffersUpgradability(
              upgradeRequest,
            );
            upgradeResult.offerUpgradableResponseItems?.forEach(
              ({ offerId, hasUpgradeOptions }) => {
                if (hasUpgradeOptions) {
                  upgradableOffers.add(offerId);
                }
              },
            );
          } catch {}
        }
        return eventDetailDataFrom(response, currentUser, upgradableOffers);
      } else {
        const response = await apiClient.eventController.getPublicEventProfile(
          eventId,
          marketplaceCode,
        );
        return publicEventDataFrom(response);
      }
    },
  });
};

export const MyYoopActions = {
  onGetUserTabs,
  onGetVirtualEventList,
  onGetInPersonEventList,
  onGetRequestedEventList,
  onGetResaleEventList,
  onGetEventDetail,
  onRevokeToken,
  onResendTokenInvite,
  onReturnToken,
  onAssignTokenToCurrentUser,
};
